/* eslint-disable id-length */
import debounce from "lodash/debounce";
import { useCallback, useMemo, useRef, useState } from "react";
import AnnotationsCount from "~/components/build/Show/components/AnnotationsCount";
import AnnotationsList from "~/components/build/Show/components/AnnotationsPanel/AnnotationsList";
import {
  FilterChips,
  FilterMenu,
  type FilterCategory,
  type SelectedFilter,
} from "~/components/build/Show/components/Filter";
import { ResizeHandle } from "~/components/build/Show/components/ResizeHandle";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { getAnnotationCounts } from "~/components/build/Show/lib/annotationCounts";
import { SCOPE_CATEGORY_KEY } from "~/components/build/Show/lib/annotationFilters";
import {
  clampAnnotationsPanelWidth,
  useBuildPreferencesStore,
} from "~/components/build/Show/lib/useBuildPreferencesStore";
import { AnnotationScope } from "~/components/build/Show/lib/types";

const RESIZE_DEBOUNCE_WAIT = 150;

export default function AnnotationsPanel() {
  const { build } = useBuild();
  const annotationCounts = getAnnotationCounts(build);
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilter[]>([]);

  const width = useBuildPreferencesStore((state) => state.annotationsPanelWidth);
  const setAnnotationsPanelWidth = useBuildPreferencesStore(
    (state) => state.setAnnotationsPanelWidth,
  );

  const panelRef = useRef<HTMLDivElement>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const persistWidth = useCallback(
    debounce((value: number) => setAnnotationsPanelWidth(value), RESIZE_DEBOUNCE_WAIT),
    [],
  );

  const resize = useCallback(
    (x: number) => {
      if (!panelRef.current) {
        return;
      }
      const rect = panelRef.current.getBoundingClientRect();
      const nextWidth = clampAnnotationsPanelWidth(rect.right - x);
      panelRef.current.style.width = `${nextWidth}px`;
      persistWidth(nextWidth);
    },
    [persistWidth],
  );

  const categories: FilterCategory[] = useMemo(
    () => [
      {
        key: SCOPE_CATEGORY_KEY,
        label: "Scope",
        noun: "scope",
        pluralNoun: "scopes",
        options: [
          {
            value: "build",
            label: "Build",
            count: annotationCounts.build,
          },
          {
            value: "job",
            label: "Job",
            count: annotationCounts.job,
          },
        ],
      },
    ],
    [annotationCounts],
  );

  // Translate the generic filter selection into the AnnotationScope[] the list expects.
  const selectedScopes = useMemo<AnnotationScope[]>(
    () =>
      selectedFilters
        .filter((filter) => filter.categoryKey === SCOPE_CATEGORY_KEY)
        .map((filter) => filter.value as AnnotationScope),
    [selectedFilters],
  );

  return (
    <div
      ref={panelRef}
      data-testid="AnnotationsPanel"
      style={{ width }}
      className="relative flex h-full min-w-0 flex-auto flex-col rounded-md border border-gray-400 bg-white md:flex-initial"
    >
      <ResizeHandle direction="horizontal" onResize={resize} className="left-[1px] rounded-l-md" />

      <div className="flex flex-auto flex-col">
        <div className="flex w-full flex-wrap items-center gap-2 border-b border-gray-400 px-2.5 py-1.5">
          <AnnotationsCount selectedFilters={selectedFilters} />
          <FilterChips
            testId="annotations-filter-chip"
            categories={categories}
            selected={selectedFilters}
            onChange={setSelectedFilters}
          />
          <div className="ml-auto">
            <FilterMenu
              testId="annotations-filter-dropdown"
              categories={categories}
              selected={selectedFilters}
              onChange={setSelectedFilters}
            />
          </div>
        </div>

        <div className="relative flex-auto">
          <div className="absolute flex h-full w-full overflow-y-auto">
            <div className="relative flex w-full flex-1 flex-col gap-2 p-2.5">
              <AnnotationsList
                scopes={selectedScopes}
                totalAnnotationsCount={annotationCounts.totalCount}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
